import { FC, useEffect, useRef, useState } from "react"
import { interpolate } from "d3"

import { addThousandsSeperator, roundToThousandsWithDecimal } from "@utils"

export type NumberInterpolatorProps = {
  duration?: number,
  useAddThousandsSeperator?: boolean,
  useRoundToThousandsWithDecimal?: boolean,
  value: number,
}

const NumberInterpolator: FC<NumberInterpolatorProps> = ({
  duration = 200,
  useAddThousandsSeperator,
  useRoundToThousandsWithDecimal,
  value,
}) => {
    const rafTimeRef = useRef<number>()
    const rafRef = useRef<number>()

    const [currentValue, setCurrentValue] = useState<number>(value)
    const [displayValue, setDisplayValue] = useState<number>(value)

    useEffect(() => {
      const _value = Number(value)

      if (_value === currentValue) return

      const interpolator = interpolate(_value, currentValue)

      const animate = (time: number) => {
        // If the animation's start time has been stashed, do the animation
        // otherwise, stash the start time
        if (rafTimeRef.current !== undefined) {
          const timeElapsed = time - rafTimeRef.current

          // If time elapsed is longer than the intended duration, reset the start time,
          // set the canonical data, and break the animation loop
          if (timeElapsed > duration) {
            rafTimeRef.current = undefined
            setDisplayValue(_value)
            setCurrentValue(_value)
            return
          }

          // Set the animated data using the startAngle and endAngle interpolators
          setDisplayValue(Math.round(interpolator(timeElapsed / duration)))

        } else {
          // Stash the start time
          rafTimeRef.current = time
        }

        // Continue the animation loop
        rafRef.current = window.requestAnimationFrame(animate)
      }

      // Start the animation loop
      rafRef.current = window.requestAnimationFrame(animate)

      // Reset and clear when something changes
      return () => {
        rafTimeRef.current = undefined
        setCurrentValue(_value)
        setDisplayValue(_value)
        if (rafRef.current || rafRef.current === 0)
          window.cancelAnimationFrame(rafRef.current)
      }
    }, [currentValue, value])

    if (useRoundToThousandsWithDecimal) return roundToThousandsWithDecimal(Math.ceil(displayValue))

    if (useAddThousandsSeperator) return addThousandsSeperator(displayValue)

    return displayValue
  }

export default NumberInterpolator
